import {CreateGuesser, EditGuesser, FieldGuesser, InputGuesser, ListGuesser, ShowGuesser} from "@api-platform/admin";
import {
    EditButton,
    ShowButton,
    AutocompleteInput,
    BooleanField,
    Count,
    DatagridConfigurable,
    DateField,
    DateInput,
    ExportButton,
    FilterButton,
    List,
    NullableBooleanInput,
    NumberField,
    ReferenceField,
    ReferenceInput,
    SearchInput,
    SelectColumnsButton,
    TextField,
    TextInput,
    BooleanInput,
    SelectInput,
    TopToolbar,
    useListContext,
    ListButton,
    useRecordContext,
    useUpdate,
    useDataProvider,
    Datagrid,
    CreateButton,
    SingleFieldList,
    ChipField,
    ReferenceArrayField,
    TabbedShowLayout,
    ArrayField,
    FunctionField,
    Show,
    ImageField,
    useRefresh,
    FormDataConsumer,
    ImageInput,
    RichTextField,
    Edit,
    SimpleForm,
    SaveButton,
    Toolbar,
    useNotify,
    useRedirect, DeleteButton, TranslatableFields,
} from 'react-admin';
import React from "react";
import {Link} from "react-router-dom";
import {Box, Button, Typography, Stack} from "@mui/material";
import {useMutation} from "react-query";
import {ColorField, ColorInput} from "react-admin-color-picker";
import {RichTextInput} from "ra-input-rich-text";

const CguListActions = () => (
    <TopToolbar>
        <SelectColumnsButton/>
        <FilterButton/>
        <CreateButton/>
    </TopToolbar>
);


const cguFilters = [
    <TextInput source={"numeroVersion"}/>,
    <TextInput source={"versionApplicationMinimumCible"}/>,
    <TextInput source={"versionApplicationIosMinimumCible"}/>,
    <SelectInput source="etat" name={"etat"} field={"etat"} choices={[
        {id: 'brouillon', name: 'Brouillon'},
        {id: 'attente_relecture', name: 'Relecture'},
        {id: 'publie', name: 'Publié'},
    ]}/>,
];

export const CguList = props => (
    <List {...props}
          actions={<CguListActions/>}
          filters={cguFilters}>
        <DatagridConfigurable rowClick={"show"}>
            <FieldGuesser source={"numeroVersion"} label={"Numéro de version CGU"}/>
            <FieldGuesser source={"versionApplicationMinimumCible"} label={"Version Android/Web minimum"}/>
            <FieldGuesser source={"versionApplicationIosMinimumCible"} label={"Version iOS minimum"}/>
            <FieldGuesser source={"etat"}/>
            <DateField source={"createdAt"} label={"Date création"}/>
            <DateField source={"datePublication"} label={"Date de publication"}/>
            <ShowButton/>
        </DatagridConfigurable>
    </List>
);

const CguStateActions = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();

    if (!record) return null;

    const updateCguState = async (newState) => {
        try {
            const updatedRecord = {...record, etat: newState};

            await dataProvider.update('cgus', {
                id: record.id,
                data: updatedRecord,
            });

            notify('État mis à jour avec succès', {type: 'success'});
            refresh();
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'état:', error);
            notify('Erreur lors de la mise à jour de l\'état', {type: 'error'});
        }
    };

    if (record.etat === 'brouillon') {
        return (
            <Button
                variant="contained"
                color="primary"
                onClick={() => updateCguState('pretPublier')}
            >
                Mettre en relecture
            </Button>
        );
    } else if (record.etat === 'attente_relecture') {
        return (
            <Stack direction="row" spacing={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => updateCguState('publier')}
                >
                    Publier
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => updateCguState('repriseRedaction')}
                >
                    Reprendre la rédaction
                </Button>
            </Stack>
        );
    } else if (record.etat === 'publie') {
        return (
            <Typography variant="body2" color="textSecondary">
                Cette CGU est publiée et ne peut plus être modifiée.
            </Typography>
        );
    }

    return null;
};

const CguShowActions = () => {
    const record = useRecordContext();

    if (!record) return null;

    return (
        <TopToolbar>
            {record.etat === 'brouillon' && <EditButton/>}
            <DeleteButton/>
        </TopToolbar>
    );
};

export const CguShow = props => (
    <Show {...props} actions={<CguShowActions/>}>
        <CguShowComponent/>
    </Show>
);

const CguShowComponent = props => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <TabbedShowLayout>
            <TabbedShowLayout.Tab label="CGU">
                <FieldGuesser source={"numeroVersion"} label={"Numéro de version CGU"}/>
                <TextField source={"etat"} label={"Etat"}/>
                <DateField source={"createdAt"} label={"Date création"}/>
                <DateField source={"datePublication"} label={"Date de publication"}/>
                <FieldGuesser source={"versionApplicationMinimumCible"} label={"Version Android/Web minimum"}/>
                <FieldGuesser source={"versionApplicationIosMinimumCible"} label={"Version iOS minimum"}/>
                <RichTextField source={"contenuHtml"} label={"Contenu"}/>
                <Box mt={2}>
                    <CguStateActions/>
                </Box>
            </TabbedShowLayout.Tab>
        </TabbedShowLayout>
    );
}

const CguEditToolbar = props => {
    const record = useRecordContext();

    const isEditable = !record || record.etat === 'brouillon';

    return (
        <Toolbar {...props}>
            {isEditable ? (
                <SaveButton/>
            ) : (
                <Typography variant="body2" color="error">
                    Cette CGU n'est plus en mode brouillon et ne peut pas être modifiée.
                </Typography>
            )}
        </Toolbar>
    );
};

export const CguEdit = props => {
    const redirect = useRedirect();
    const notify = useNotify();

    const handleSubmit = async (data) => {
        if (data.etat !== 'brouillon') {
            notify('Cette CGU n\'est plus en mode brouillon et ne peut pas être modifiée', {type: 'warning'});
            redirect('show', 'cgu', data.id);
            return false;
        }
        return data;
    };

    return (
        <Edit {...props} transform={handleSubmit}>
            <SimpleForm toolbar={<CguEditToolbar/>}>
                <TextInput source={"numeroVersion"} label={"Numéro de version CGU"}/>
                <TextInput source={"versionApplicationMinimumCible"} label={"Version Android/Web minimum"} helperText={"Le numéro de version android/web doit être sous cette forme : 34"}/>
                <TextInput source={"versionApplicationIosMinimumCible"} label={"Version iOS minimum"} helperText={"Le numéro de version iOS doit contenir au moins trois parties (ex: 1.0.9)."}/>
                <RichTextInput source={"contenuHtml"} multiline={true} rows={10} fullWidth={true} name={"contenuHtml"}
                               label={"Contenu"}/>
                <Box mt={2}>
                    <CguStateActions/>
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export const CguCreate = props => (
    <CreateGuesser {...props}>
        <TextInput source={"numeroVersion"} label={"Numéro de version CGU"}/>
        <TextInput source={"versionApplicationMinimumCible"} label={"Version Android/Web minimum"} helperText={"Le numéro de version android/web doit être sous cette forme : 34"}/>
        <TextInput source={"versionApplicationIosMinimumCible"} label={"Version iOS minimum"} helperText={"Le numéro de version iOS doit contenir au moins trois parties (ex: 1.0.9)."}/>
        <RichTextInput source={"contenuHtml"} multiline={true} rows={10} fullWidth={true} name={"contenuHtml"}
                       label={"Contenu"}/>
    </CreateGuesser>
);
